import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Heading,
  IconButton,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { ChevronDown, ChevronUp } from "lucide-react";

const formatDateTime = (isoString) => {
  if (!isoString) return "-";

  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatMessageType = (value) => {
  if (!value) return "-";
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const sendStatusColor = (status) => {
  if (status === "sent") return "green";
  if (status === "failed") return "red";
  return "gray";
};

const twilioStatusColor = (status) => {
  if (status === "delivered") return "green";
  if (["failed", "undelivered"].includes(status)) return "red";
  if (["sent", "queued", "accepted", "sending"].includes(status)) return "blue";
  return "gray";
};

export default function TextMessagesTable() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    q: "",
    message_type: "",
    send_status: "",
    twilio_status: "",
    from: "",
    to: "",
    sort: "newest",
  });

  const loadMessages = async (nextPage = page) => {
    setLoading(true);

    try {
      const params = new URLSearchParams({ page: String(nextPage) });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });

      const response = await fetch(`/api/text_messages?${params.toString()}`);
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || "Failed to load text messages");
      }

      setData(payload);
      setPage(nextPage);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load text messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages(1);
  }, [filters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      q: "",
      message_type: "",
      send_status: "",
      twilio_status: "",
      from: "",
      to: "",
      sort: "newest",
    });
  };

  const toggleExpanded = (messageId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const messages = data?.messages || [];
  const pagination = data?.pagination || {};
  const messageTypes = data?.filters?.message_types || [];
  const sendStatuses = data?.filters?.send_statuses || [];
  const twilioStatuses = data?.filters?.twilio_statuses || [];

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={{ base: 4, md: 6 }} bg="white">
      <HStack justify="space-between" align="start" flexWrap="wrap" spacing={3} mb={4}>
        <Box>
          <Heading size="sm">Text Messages</Heading>
          <Text mt={1} fontSize="sm" color="gray.600">
            Outbound waitlist texts sent by Same Day, including Twilio delivery status when available.
          </Text>
        </Box>
        <Button size="sm" variant="outline" onClick={() => loadMessages(page)} isLoading={loading} minH="44px" w={{ base: "full", sm: "auto" }}>
          Refresh
        </Button>
      </HStack>

      <Grid templateColumns={{ base: "1fr", md: "repeat(7, 1fr)" }} gap={3} mb={4}>
        <Input
          size="sm"
          placeholder="Search customer, phone, body, SID"
          value={filters.q}
          onChange={(event) => updateFilter("q", event.target.value)}
        />
        <Select size="sm" value={filters.message_type} onChange={(event) => updateFilter("message_type", event.target.value)}>
          <option value="">All types</option>
          {messageTypes.map((type) => (
            <option key={type} value={type}>{formatMessageType(type)}</option>
          ))}
        </Select>
        <Select size="sm" value={filters.send_status} onChange={(event) => updateFilter("send_status", event.target.value)}>
          <option value="">All send statuses</option>
          {sendStatuses.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </Select>
        <Select size="sm" value={filters.twilio_status} onChange={(event) => updateFilter("twilio_status", event.target.value)}>
          <option value="">All delivery statuses</option>
          {twilioStatuses.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </Select>
        <Select size="sm" value={filters.sort} onChange={(event) => updateFilter("sort", event.target.value)}>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="status_updated">Latest delivery update</option>
        </Select>
        <Input size="sm" type="date" value={filters.from} onChange={(event) => updateFilter("from", event.target.value)} />
        <HStack spacing={2}>
          <Input size="sm" type="date" value={filters.to} onChange={(event) => updateFilter("to", event.target.value)} />
          <Button size="sm" variant="ghost" onClick={clearFilters}>Clear</Button>
        </HStack>
      </Grid>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <HStack spacing={3} color="gray.600">
          <Spinner size="sm" />
          <Text fontSize="sm">Loading text messages...</Text>
        </HStack>
      ) : messages.length > 0 ? (
        <>
          <VStack display={{ base: "flex", md: "none" }} align="stretch" spacing={3}>
            {messages.map((message) => {
              const isExpanded = expandedIds.has(message.id);
              return (
                <Box key={message.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
                  <Flex justify="space-between" align="start" gap={3}>
                    <Box minW={0}>
                      <Text fontWeight="semibold" noOfLines={1}>{message.client?.name || message.to_number || "Unknown customer"}</Text>
                      <Text fontSize="xs" color="gray.500">{formatDateTime(message.sent_at || message.created_at)} · {formatMessageType(message.message_type)}</Text>
                    </Box>
                    <IconButton
                      aria-label={isExpanded ? "Hide text message details" : "Show text message details"}
                      icon={isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      minW="44px"
                      minH="44px"
                      variant="ghost"
                      onClick={() => toggleExpanded(message.id)}
                    />
                  </Flex>
                  <HStack mt={3} spacing={2} flexWrap="wrap">
                    <Badge colorScheme={sendStatusColor(message.send_status)}>{message.send_status}</Badge>
                    <Badge colorScheme={twilioStatusColor(message.twilio_status)}>{message.twilio_status || "unknown"}</Badge>
                  </HStack>
                  <Text mt={3} fontSize="sm" color="gray.700" noOfLines={isExpanded ? undefined : 2} whiteSpace={isExpanded ? "pre-wrap" : "normal"}>{message.body}</Text>
                  {isExpanded && (
                    <VStack mt={4} pt={4} borderTop="1px solid" borderColor="gray.100" align="stretch" spacing={3}>
                      <Box><Text fontSize="xs" color="gray.500">To</Text><Text fontSize="sm">{message.to_number || "-"}</Text></Box>
                      <Box><Text fontSize="xs" color="gray.500">Related</Text>{message.trackable?.url ? <Button as="a" href={message.trackable.url} size="sm" variant="link" colorScheme="blue">{message.trackable.label}</Button> : <Text fontSize="sm">{message.trackable?.label || "-"}</Text>}</Box>
                      <Box><Text fontSize="xs" color="gray.500">Twilio SID</Text><Text fontSize="sm" overflowWrap="anywhere">{message.twilio_message_sid || "-"}</Text></Box>
                      <Box><Text fontSize="xs" color="gray.500">Error</Text><Text fontSize="sm" color="red.700">{[message.twilio_error_code, message.twilio_error_message].filter(Boolean).join(": ") || "-"}</Text></Box>
                    </VStack>
                  )}
                </Box>
              );
            })}
          </VStack>
          <Box overflowX="auto" display={{ base: "none", md: "block" }}>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Sent</Th>
                  <Th>Customer</Th>
                  <Th>To</Th>
                  <Th>Type</Th>
                  <Th>Send</Th>
                  <Th>Delivery</Th>
                  <Th>Related</Th>
                  <Th>Body</Th>
                  <Th textAlign="right">Details</Th>
                </Tr>
              </Thead>
              <Tbody>
                {messages.map((message) => {
                  const isExpanded = expandedIds.has(message.id);

                  return (
                    <React.Fragment key={message.id}>
                      <Tr>
                        <Td whiteSpace="nowrap">{formatDateTime(message.sent_at || message.created_at)}</Td>
                        <Td whiteSpace="nowrap">{message.client?.name || "-"}</Td>
                        <Td whiteSpace="nowrap">{message.to_number}</Td>
                        <Td whiteSpace="nowrap">{formatMessageType(message.message_type)}</Td>
                        <Td>
                          <Badge colorScheme={sendStatusColor(message.send_status)}>{message.send_status}</Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={twilioStatusColor(message.twilio_status)}>{message.twilio_status || "unknown"}</Badge>
                        </Td>
                        <Td whiteSpace="nowrap">
                          {message.trackable?.url ? (
                            <Button as="a" href={message.trackable.url} size="xs" variant="link" colorScheme="blue">
                              {message.trackable.label}
                            </Button>
                          ) : (
                            <Text fontSize="sm" color="gray.600">{message.trackable?.label || "-"}</Text>
                          )}
                        </Td>
                        <Td maxW="280px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {message.body}
                        </Td>
                        <Td textAlign="right">
                          <IconButton
                            aria-label={isExpanded ? "Hide text message details" : "Show text message details"}
                            icon={isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            size="xs"
                            variant="ghost"
                            onClick={() => toggleExpanded(message.id)}
                          />
                        </Td>
                      </Tr>
                      {isExpanded && (
                        <Tr>
                          <Td colSpan={9} bg="gray.50">
                            <VStack align="stretch" spacing={3}>
                              <Box>
                                <Text fontSize="xs" color="gray.600" fontWeight="semibold" mb={1}>Message Body</Text>
                                <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">{message.body}</Text>
                              </Box>
                              <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={3}>
                                <Box>
                                  <Text fontSize="xs" color="gray.600" fontWeight="semibold">Twilio SID</Text>
                                  <Text fontSize="sm" color="gray.800">{message.twilio_message_sid || "-"}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="xs" color="gray.600" fontWeight="semibold">Status Updated</Text>
                                  <Text fontSize="sm" color="gray.800">{formatDateTime(message.status_updated_at)}</Text>
                                </Box>
                                <Box>
                                  <Text fontSize="xs" color="gray.600" fontWeight="semibold">Error</Text>
                                  <Text fontSize="sm" color="red.700">
                                    {[message.twilio_error_code, message.twilio_error_message].filter(Boolean).join(": ") || "-"}
                                  </Text>
                                </Box>
                              </Grid>
                            </VStack>
                          </Td>
                        </Tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          <HStack justify="space-between" mt={4} flexWrap="wrap" spacing={3}>
            <Text fontSize="sm" color="gray.600">
              Showing page {pagination.page || 1} of {pagination.total_pages || 1}
              {" "}({pagination.total_count || 0} total)
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => loadMessages(Math.max(1, page - 1))}
                isDisabled={(pagination.page || 1) <= 1 || loading}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => loadMessages(page + 1)}
                isDisabled={loading || (pagination.page || 1) >= (pagination.total_pages || 1)}
              >
                Next
              </Button>
            </HStack>
          </HStack>
        </>
      ) : (
        <Text color="gray.600" fontSize="sm">No text messages found.</Text>
      )}
    </Box>
  );
}
